export interface SpeakerNote {
  headline: string;
  bullets: string[];
  timing?: string; // suggested time on this screen
  transition?: string; // one-liner to bridge to next screen
}

/**
 * Route-keyed speaker notes for WeKIT Education Intelligence™ demos.
 * Keys match pathname prefixes; longest match wins.
 */
export const SPEAKER_NOTES: Record<string, SpeakerNote> = {
  "/schools": {
    headline: "Principal Overview — the executive lens",
    timing: "~90 seconds",
    bullets: [
      "Open with the AY 2026–27 badge — signals this is a live, current cohort.",
      "Point to the KPI row: engagement, wellbeing, career-clarity index, at-risk count.",
      "Hover the heatmap to show cohort-level intervention hot-spots.",
      "Read one AI insight aloud and mention it updates as filters change.",
    ],
    transition: "Let me zoom into one learner so you can see how this rolls up.",
  },
  "/schools/student": {
    headline: "Student Profile — the learner lens",
    timing: "~75 seconds",
    bullets: [
      "This is Priya, Grade 10, Ravenclaw house — a real composite from pilots.",
      "Show the Career DNA radar and the four WCC360 pillars.",
      "Highlight the recommended next studio session.",
      "Mention the printable one-pager for parent-teacher meetings.",
    ],
    transition: "Every learner profile also opens a family view — here's the parent side.",
  },
  "/schools/parent": {
    headline: "Parent Dashboard — family engagement",
    timing: "~60 seconds",
    bullets: [
      "Parents see progress, not raw scores — reduces anxiety.",
      "One-tap actions: message counsellor, book a session, download brief.",
      "Weekly digest email is opt-in and school-branded.",
    ],
    transition: "The counsellor sees the same student — but prioritised by need.",
  },
  "/schools/counsellor": {
    headline: "Counsellor — prioritised caseload",
    timing: "~60 seconds",
    bullets: [
      "The caseload is AI-sorted by risk and momentum, not alphabetical.",
      "Show the 'suggested action' column — this is where AI earns its keep.",
      "Weekly brief one-pager is printable for case reviews.",
    ],
    transition: "Now let's look at how learning actually happens — the Studios.",
  },
  "/schools/studios": {
    headline: "Learning Studios — WALS™ · WAFLE™ · WAFFS™",
    timing: "~60 seconds",
    bullets: [
      "Three studio types: Life Skills, Future Learning, Foundational Skills.",
      "Enrolment, completion, and outcome scores in one view.",
      "AI insight surfaces which studio is producing the biggest lift.",
    ],
    transition: "And every studio outcome flows into the learner's portfolio.",
  },
  "/schools/portfolio": {
    headline: "Portfolio — the digital learner profile",
    timing: "~45 seconds",
    bullets: [
      "Auto-assembled from assessments, studios, and mentor feedback.",
      "Shareable link for university applications and internships.",
      "Version history means nothing is ever lost.",
    ],
    transition: "Let me close by showing what the board pack looks like.",
  },
  "/schools/reports": {
    headline: "Reports — share the story",
    timing: "~60 seconds",
    bullets: [
      "One-click exports for board packs, parent nights, and press.",
      "Every chart is source-linked so you can drill down.",
      "This is the artefact principals bring to trustee meetings.",
    ],
    transition: "That's the seven-minute tour — happy to go deeper on any screen.",
  },
};

export function noteForPath(pathname: string): SpeakerNote | null {
  const keys = Object.keys(SPEAKER_NOTES).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (k === "/schools" ? pathname === k : pathname.startsWith(k)) {
      return SPEAKER_NOTES[k];
    }
  }
  return null;
}
